import { t } from '@trimble/i18n';
import {
  Accessor,
  batch,
  createContext,
  createEffect,
  createMemo,
  createResource,
  createSignal,
  JSX,
  on,
  onCleanup,
  untrack,
  useContext,
} from 'solid-js';
import { createStore, produce, SetStoreFunction } from 'solid-js/store';
import { useQueryClient } from '@tanstack/solid-query';
import { useConnectProject } from '@trimble/connect-project-providers';
import { useApiServices } from '../ApiServiceProvider';
import {
  FetchableItems,
  isItemLoadingOrLoaded,
  LoadState,
} from '@trimble/data-loading';
import { AlignmentReloadScheduler } from './AlignmentReloadScheduler';
import { CrsIssueKind } from '@trimble/project-data-panel-types';
import {
  ActiveItems,
  ClassificationSchemeItem,
  defaultProjectDataState,
  FileFolderItem,
  MissionItem,
  PointCloudItem,
  ProjectDataState,
  TrajectoryItem,
} from './DataStateModels';
import { DataLoadingService } from './DataLoadingService';
import {
  ClassStatistics,
  AlignmentSettings,
  FormattingSettings,
  PointCloudClassification,
} from '@trimble/types';
import type { ListAlignmentsResponse } from '@data-access/geospatial/alignments';
import {
  mapToPointcloudStatus,
  type Pointcloud,
} from '@data-access/geospatial/scan-data';
import { ColorGenerator, Point2D, stringsToSeed } from '@trimble/utilities';
import { useLogger } from '@trimble/solidjs-utils';
import { NotificationEventBus } from '@trimble/solidjs-notifications';
import { resolveGaussianSplatQueryUpdate } from './build-gaussian-splat-list-items';
import { useSplatsListQuery } from '../splats/splats-api-service';
import { UnreachableCaseError } from 'ts-essentials';
import { useProjectSettings } from '../ProjectSettingsProvider';
import {
  getVisiblePointCloudIds,
  handleQueryError,
} from './DataContextProviderHelpers';
import { createPointCloudCrsState } from './point-cloud-crs-state';
import type { ActiveCrs, PointCloudCrs } from './point-cloud-crs.types';
import {
  resolveProjectGeoReference,
  type AxisAlignedBounds,
} from './project-geo-reference';
import {
  pickEffectiveGeoReference,
  pickFirstLoadedPcGeo,
} from './geo-reference-merge';
import {
  IndexedDbProjectDataCache,
  ProjectDataRepository,
  TcpsProjectDataSource,
  type ProjectDataSnapshotSource,
} from '@trimble/project-data-repository';
import type { Client } from '@hey-api/client-fetch';
import type { TcRegion } from '@data-access/connect/tc-tcps';

const PROJECT_DATA_LOAD_ATTEMPTS = 3;

type ProjectFileFolderSnapshot = {
  readonly repository: ProjectDataRepository;
  readonly items: Record<string, FileFolderItem>;
};

function throwIfAborted(signal: AbortSignal): void {
  if (!signal.aborted) return;
  const reason = signal.reason;
  throw reason instanceof Error
    ? reason
    : new DOMException('The operation was aborted.', 'AbortError');
}

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

async function loadProjectFileFolderSnapshot(options: {
  readonly region: TcRegion;
  readonly projectId: string;
  readonly signal: AbortSignal;
  getTcpsRegionClient(region: TcRegion): Promise<Client>;
  getProjectDetails(
    region: TcRegion,
    projectId: string
  ): Promise<{ readonly rootId: string }>;
  attachStore(store: ProjectDataRepository): void;
}): Promise<ProjectFileFolderSnapshot> {
  throwIfAborted(options.signal);
  const [client, projectDetails] = await Promise.all([
    options.getTcpsRegionClient(options.region),
    options.getProjectDetails(options.region, options.projectId),
  ]);
  throwIfAborted(options.signal);
  const tcpsSource = new TcpsProjectDataSource(
    client,
    options.region,
    projectDetails.rootId
  );

  let lastError: unknown;
  for (let attempt = 0; attempt < PROJECT_DATA_LOAD_ATTEMPTS; attempt++) {
    throwIfAborted(options.signal);
    const stagedItems: Record<string, FileFolderItem> = {};
    const source: ProjectDataSnapshotSource = {
      fetchPage: async (request) => {
        throwIfAborted(options.signal);
        const page = await tcpsSource.fetchPage(request);
        throwIfAborted(options.signal);
        for (const item of page.items) {
          if (item.isDeleted) continue;
          stagedItems[item.id] = {
            ...item,
            loadState: LoadState.NotLoaded,
          };
        }
        return page;
      },
    };
    const repository = new ProjectDataRepository({
      projectId: options.projectId,
      rootId: projectDetails.rootId,
      region: options.region.location,
      source,
      cache: IndexedDbProjectDataCache.isSupported()
        ? new IndexedDbProjectDataCache()
        : undefined,
    });
    options.attachStore(repository);
    try {
      await repository.initialize({ allPages: true });
      throwIfAborted(options.signal);
      return { repository, items: { ...stagedItems } };
    } catch (error: unknown) {
      repository.dispose();
      throwIfAborted(options.signal);
      if (isAbortError(error)) throw error;
      lastError = error;
    }
  }

  throw lastError instanceof Error
    ? lastError
    : new Error('Failed to load project file snapshot');
}
export function DataContextProvider(props: {
  children: JSX.Element;
}): JSX.Element {
  const logger = useLogger().getLogger('DataContextProvider');

  const [dataState, setDataState] = createStore<ProjectDataState>(
    defaultProjectDataState
  );

  const [activeItems, setActiveItems] = createStore<ActiveItems>({
    alignmentId: null,
    pointCloudTrnId: null,
  });

  const [classificationSchemeState, setClassificationSchemeState] = createStore<
    FetchableItems<ClassificationSchemeItem>
  >({
    state: LoadState.NotLoaded,
  });

  const { selectedTenantTrn, selectedRegion, selectedProject } =
    useConnectProject();

  const {
    alignmentApi,
    scanDataApi,
    tcTcpsApi,
    mobileMappingApi,
    splatsApi,
    getTcpsRegionClient,
    coordinatesClient,
  } = useApiServices();

  const { formattingSettings, alignmentSettings, alignmentRefreshVersion } =
    useProjectSettings();

  const [projectDataRepository, setProjectDataRepository] =
    createSignal<ProjectDataRepository>();

  // Csib of loaded point clouds, written by the load flow.
  const [loadedCsibs, setLoadedCsibs] = createSignal<
    ReadonlyMap<string, string>
  >(new Map());

  const setLoadedCsib = (itemId: string, csib: string | undefined): void => {
    setLoadedCsibs((prev) => {
      const hasItem = prev.has(itemId);
      if (csib === undefined) {
        if (!hasItem) return prev;
        const next = new Map(prev);
        next.delete(itemId);
        return next;
      }
      if (prev.get(itemId) === csib) return prev;
      const next = new Map(prev);
      next.set(itemId, csib);
      return next;
    });
  };

  // World-coordinate center of loaded point clouds (bounding box midpoint).
  const [loadedCenters, setLoadedCenters] = createSignal<
    ReadonlyMap<string, Point2D>
  >(new Map());

  const setLoadedCenter = (
    itemId: string,
    center: Point2D | undefined
  ): void => {
    setLoadedCenters((prev) => {
      const hasItem = prev.has(itemId);
      if (center === undefined) {
        if (!hasItem) return prev;
        const next = new Map(prev);
        next.delete(itemId);
        return next;
      }
      const existing = prev.get(itemId);
      if (
        existing !== undefined &&
        existing.x === center.x &&
        existing.y === center.y
      ) {
        return prev;
      }
      const next = new Map(prev);
      next.set(itemId, center);
      return next;
    });
  };

  // Per-cloud CRS warnings for tree-node status (missing CSIB / mismatch).
  const [loadedCrsIssues, setLoadedCrsIssues] = createSignal<
    ReadonlyMap<string, CrsIssueKind>
  >(new Map());

  const setLoadedCrsIssue = (
    itemId: string,
    issue: CrsIssueKind | undefined
  ): void => {
    setLoadedCrsIssues((prev) => {
      const hasItem = prev.has(itemId);
      if (issue === undefined) {
        if (!hasItem) return prev;
        const next = new Map(prev);
        next.delete(itemId);
        return next;
      }
      if (prev.get(itemId) === issue) return prev;
      const next = new Map(prev);
      next.set(itemId, issue);
      return next;
    });
  };

  // A hidden cloud must not own the active CRS — it would tag a visible
  // cloud drawn in a different CRS.
  const visiblePointCloudIds = createMemo<ReadonlySet<string>>(
    () => new Set(getVisiblePointCloudIds(dataState) ?? [])
  );

  const firstLoadedPcGeo = createMemo(() =>
    pickFirstLoadedPcGeo(loadedCsibs(), loadedCenters(), visiblePointCloudIds())
  );

  const projectGeoResourceKey = createMemo(() => {
    const project = selectedProject();
    const csib64 = project?.crs?.csib64?.trim();
    if (!project || !csib64) {
      return undefined;
    }
    return {
      projectId: project.id,
      csib64,
      geometry: project.address?.geometry,
    };
  });

  const [resolvedProjectGeo] = createResource(
    projectGeoResourceKey,
    async (key) =>
      resolveProjectGeoReference(key.csib64, key.geometry, coordinatesClient)
  );

  const projectGeoReference = createMemo(() => resolvedProjectGeo()?.geo);

  const effectiveGeoReference = createMemo(() =>
    pickEffectiveGeoReference(
      firstLoadedPcGeo()?.csib,
      firstLoadedPcGeo()?.center,
      projectGeoReference()
    )
  );

  const currentLoadedCsib = createMemo(() => effectiveGeoReference()?.csib);

  const projectCsib64 = createMemo(() => projectGeoResourceKey()?.csib64);

  const { activeCrs, pointCloudCrsById } = createPointCloudCrsState({
    loadedCsibs,
    loadedCenters,
    visiblePointCloudIds,
    projectCsib64,
    tenantTrn: selectedTenantTrn,
    coordinatesClient,
    queryClient: useQueryClient(),
    logger,
  });

  const currentLoadedCenter = createMemo<Point2D | undefined>(
    () => effectiveGeoReference()?.center,
    undefined,
    {
      equals: (a, b) =>
        a === b ||
        (a !== undefined && b !== undefined && a.x === b.x && a.y === b.y),
    }
  );

  const currentLoadedBounds = createMemo<AxisAlignedBounds | undefined>(() => {
    if (firstLoadedPcGeo() !== undefined) {
      return undefined;
    }
    return resolvedProjectGeo()?.bounds;
  });

  const dataLoadingService = new DataLoadingService(
    dataState,
    setDataState,
    setLoadedCsib,
    setLoadedCenter,
    setLoadedCrsIssue
  );

  createEffect(
    on(
      () => selectedProject()?.id,
      (projectId, prevProjectId) => {
        if (projectId === prevProjectId) {
          return;
        }
        dataLoadingService.cancelCrsMismatchChecks();
        setLoadedCsibs(new Map());
        setLoadedCenters(new Map());
        setLoadedCrsIssues(new Map());
      },
      { defer: true }
    )
  );

  createEffect(
    on(
      selectedTenantTrn,
      (tenantTrn, prevTenantTrn) => {
        // defer skips mount; do not also require prev != null — on()+defer leaves
        // prevInput unset until after the first callback, which hid the first switch.
        if (tenantTrn !== prevTenantTrn) {
          dataLoadingService.cancelAllLoads();
        }
      },
      { defer: true }
    )
  );
  onCleanup(() => dataLoadingService.cancelAllLoads());
  const [isScanDataRegionSupported] = createResource(
    selectedRegion,
    async (region) => {
      return scanDataApi.isScanDataRegionSupported(region);
    }
  );

  const isRegionExplicitlyUnsupported = createMemo(
    () =>
      isScanDataRegionSupported.state === 'ready' &&
      isScanDataRegionSupported() === false
  );

  const isRegionSupportCheckResolved = createMemo(
    () =>
      isScanDataRegionSupported.state === 'ready' ||
      isScanDataRegionSupported.state === 'errored'
  );

  const canFetchScanData = createMemo(
    () => isRegionSupportCheckResolved() && !isRegionExplicitlyUnsupported()
  );

  const unsupportedContextKey = createMemo(() => {
    if (!isRegionExplicitlyUnsupported()) return undefined;
    const region = selectedRegion();
    const project = selectedProject();
    if (region === undefined || project === undefined) return undefined;

    return `${region.location}:${project.id}`;
  });

  createEffect(
    on(unsupportedContextKey, (key, prevKey) => {
      if (key === undefined || key === prevKey) return;

      NotificationEventBus.emit({
        type: 'info',
        message: t('mobileMapping.dataNotAvailableInSelectedRegion'),
      });

      setDataState('pointCloud', { state: LoadState.Error });
      setDataState('mission', { state: LoadState.Error });
      setDataState('trajectory', { state: LoadState.Error });
      setClassificationSchemeState({ state: LoadState.Error });
    })
  );

  const missionsQuery = createMemo(() => {
    const tenantTrn = selectedTenantTrn();
    if (tenantTrn === undefined || !canFetchScanData()) return undefined;
    return mobileMappingApi.listMissionsTreeQuery(selectedTenantTrn);
  });

  createEffect(
    on(
      [
        () => missionsQuery()?.status,
        () => missionsQuery()?.isFetching,
        missionsQuery,
      ],
      ([_, _isFetching, query]) => {
        if (!query) return;

        if (query.isError) {
          return handleQueryError('mission', setDataState, query.error);
        }
        // set datastate to loading on initial load, refetching replaces data.
        if (query.isLoading) {
          setDataState({
            mission: {
              state: LoadState.Loading,
            },
          });
          return;
        }

        if (query.data?.missions) {
          const missions = query.data.missions.reduce<
            Record<string, MissionItem>
          >((acc, mission) => {
            if (
              mission.mission.id == null ||
              mission.mission.missionTrn == null
            )
              return acc;

            // Generate deterministic colors for this mission's runs
            const missionName = mission.mission.name ?? '';
            const runNames = mission.runs.map((run) => run.name ?? '');
            const seed = stringsToSeed(missionName, ...runNames);
            const colorGenerator = new ColorGenerator(seed);
            const colors = colorGenerator.generateColors(mission.runs.length);

            acc[mission.mission.id] = {
              id: mission.mission.id,
              name: missionName,
              missionTrn: mission.mission.missionTrn!,
              runs: mission.runs
                .filter((run) => run.pointCloudTrn != null && run.id != null)
                .map((run, index) => ({
                  id: run.pointCloudTrn!,
                  runId: run.id!,
                  name: run.name ?? '',
                  beginStation: run.startStation ?? 0,
                  endStation: run.endStation ?? 1, // make it invalid if undefined
                  color: {
                    r: colors[index].r,
                    g: colors[index].g,
                    b: colors[index].b,
                  },
                  status: run.status,
                })),
              pathId: mission.mission.alignmentTrn,
              loadState: LoadState.NotLoaded,
            };
            return acc;
          }, {});
          logger.debug(
            `loaded missions, count: ${Object.keys(missions).length}`,
            missions
          );

          setDataState({
            mission: {
              state: LoadState.LoadComplete,
              items: missions,
            },
          });
        }
      }
    )
  );

  const alignmentsQuery = createMemo(() => {
    const tenantTrn = selectedTenantTrn();
    if (tenantTrn === undefined || !canFetchScanData()) return undefined;

    return alignmentApi.listAlignmentsQuery(selectedTenantTrn);
  });

  const alignmentsWithMissionContextQuery = createMemo(() => {
    const alignmentsQueryResult = alignmentsQuery();
    if (alignmentsQueryResult === undefined) {
      return undefined;
    }

    const alignments =
      alignmentsQueryResult.data || ([] as ListAlignmentsResponse);
    const missions =
      dataState.mission.state === LoadState.LoadComplete
        ? Object.values(dataState.mission.items)
        : [];
    return {
      status:
        alignmentsQueryResult.status === 'pending' ||
        missionsQuery()?.status === 'pending'
          ? 'pending'
          : alignmentsQueryResult.status === 'error' ||
              missionsQuery()?.status === 'error'
            ? 'error'
            : 'success',
      isError: alignmentsQueryResult.isError,
      isLoading: alignmentsQueryResult.isLoading,
      isFetching: alignmentsQueryResult.isFetching,
      data: alignments.map((alignment) => {
        const mission = missions.find((m) => m.pathId === alignment.id);
        if (mission) {
          logger.debug(`found mission for alignment`, {
            missionName: mission.name,
            missionId: mission.pathId,
            alignmentName: alignment.name,
            alignmentId: alignment.id,
          });
        }
        return {
          ...alignment,
          mission,
        } as TrajectoryItem;
      }),
      error: alignmentsQueryResult.error,
    };
  });

  createEffect(
    on(
      [
        () => alignmentsWithMissionContextQuery()?.status,
        () => alignmentsWithMissionContextQuery()?.isFetching,
        alignmentsWithMissionContextQuery,
      ],
      ([_, _isFetching, query]) => {
        if (!query) return;

        if (query.isError) {
          return handleQueryError('trajectory', setDataState, query.error);
        }

        // set datastate to loading on initial load, refetching replaces data.
        if (query.isLoading) {
          setDataState({
            trajectory: {
              state: LoadState.Loading,
            },
          });
          return;
        }

        if (query.data != null) {
          const trajectories = query.data.reduce<
            Record<string, TrajectoryItem>
          >((acc, t) => {
            if (
              t.id == null ||
              t.beginStation == null ||
              t.endStation == null ||
              t.sourceFileId == null ||
              t.name == null
            ) {
              return acc;
            }
            acc[t.id] = {
              id: t.id,
              loadState: LoadState.NotLoaded,
              name: t.name,
              beginStation: t.beginStation,
              endStation: t.endStation,
              sourceFileId: t.sourceFileId,
              mission: t.mission,
              isSplittable: undefined,
            };
            return acc;
          }, {});
          setDataState({
            trajectory: {
              state: LoadState.LoadComplete,
              items: trajectories,
            },
          });
        }
      }
    )
  );

  // Add point clouds to state.
  const pointCloudsQuery = createMemo(() => {
    const region = selectedRegion();
    const project = selectedProject();
    if (region === undefined || project === undefined || !canFetchScanData()) {
      return undefined;
    }

    return scanDataApi.listPointCloudsQuery(region, project.id);
  });

  // when a run is loaded/unloaded, we need to update the loaded sections of the associated trajectory
  const pointCloudsWithMissionContextQuery = createMemo(() => {
    const pointCloudsQueryResult = pointCloudsQuery();
    if (pointCloudsQueryResult === undefined) {
      return undefined;
    }

    const pointClouds =
      pointCloudsQueryResult.data?.items || ([] as Pointcloud[]);
    const missions =
      dataState.mission.state === LoadState.LoadComplete
        ? Object.values(dataState.mission.items)
        : [];
    return {
      status:
        pointCloudsQueryResult.status === 'pending' ||
        missionsQuery()?.status === 'pending'
          ? 'pending'
          : pointCloudsQueryResult.status === 'error' ||
              missionsQuery()?.status === 'error'
            ? 'error'
            : 'success',
      isError: pointCloudsQueryResult.isError,
      isLoading: pointCloudsQueryResult.isLoading,
      isFetching: pointCloudsQueryResult.isFetching,
      data: {
        items: pointClouds
          .filter((pointCloud) => !!pointCloud.pointCount)
          .map((pointCloud) => {
            const mission = missions.find((m) =>
              m.runs.find((run) => run.id === pointCloud.id)
            );
            if (mission) {
              logger.debug(`found mission for point cloud`, {
                missionName: mission.name,
                missionId: mission.pathId,
                pointCloudName: pointCloud.name,
                pointCloudId: pointCloud.id,
              });
            } else {
              logger.debug(`no mission found for point cloud`, {
                pointCloudName: pointCloud.name,
                pointCloudId: pointCloud.id,
              });
            }
            return {
              ...pointCloud,
              mission,
            } as Pointcloud & { mission?: MissionItem };
          }),
      },
      error: pointCloudsQueryResult.error,
    };
  });

  createEffect(
    on(
      [
        () => pointCloudsWithMissionContextQuery()?.status,
        () => pointCloudsWithMissionContextQuery()?.isFetching,
        pointCloudsWithMissionContextQuery,
      ],
      ([_, _isFetching, query]) => {
        if (!query) return;

        if (query.isError) {
          return handleQueryError('pointCloud', setDataState, query.error);
        }

        // set datastate to loading on initial load, refetching replaces data.
        if (query.isLoading) {
          setDataState({
            pointCloud: {
              state: LoadState.Loading,
            },
          });
          return;
        }

        if (query.data.items) {
          logger.debug(
            `point clouds loaded, count: ${query.data.items.length}`,
            query.data.items
          );

          // Log point clouds that don't belong to a mission
          const pointCloudsWithoutMission = query.data.items.filter(
            (pc) => !pc.mission
          );
          if (pointCloudsWithoutMission.length > 0) {
            logger.debug(
              `found point cloud(s) without a mission, count: ${pointCloudsWithoutMission.length}`,
              pointCloudsWithoutMission
            );
          }

          const pointClouds = query.data.items.reduce<
            Record<string, PointCloudItem>
          >((acc, pointCloud) => {
            if (pointCloud.id == null) return acc;

            acc[pointCloud.id] = {
              id: pointCloud.id,
              name: pointCloud.name ?? '',
              status: mapToPointcloudStatus(pointCloud.status ?? 'InProgress'),
              pointCount: pointCloud.pointCount ?? 0,
              mission: pointCloud.mission,
              classStatistics: pointCloud.classStatistics,
              stationTransform: pointCloud.stationTransform,
              loadState: LoadState.NotLoaded,
            };
            return acc;
          }, {});

          logger.debug(
            `point clouds in DataState, count: ${Object.keys(pointClouds).length}`,
            pointClouds
          );
          setDataState({
            pointCloud: {
              state: LoadState.LoadComplete,
              items: pointClouds,
            },
          });
        }
      }
    )
  );

  const splatsListQuery = useSplatsListQuery(
    () => splatsApi,
    selectedTenantTrn,
    () => selectedProject()?.id
  );

  const splatsListQueryEnabled = createMemo(
    () =>
      splatsApi !== undefined &&
      selectedTenantTrn() !== undefined &&
      selectedProject()?.id !== undefined
  );

  createEffect(
    on(
      [
        splatsListQueryEnabled,
        () => splatsListQuery.status,
        () => splatsListQuery.isFetching,
      ],
      ([enabled, _status, _isFetching]) => {
        const existingItems = untrack(() =>
          dataState.gaussianSplat.state === LoadState.LoadComplete
            ? dataState.gaussianSplat.items
            : undefined
        );
        const update = resolveGaussianSplatQueryUpdate(
          enabled
            ? {
                isError: splatsListQuery.isError,
                isLoading: splatsListQuery.isLoading,
                data: splatsListQuery.data,
              }
            : undefined,
          existingItems
        );

        switch (update.kind) {
          case 'cleared':
            setDataState('gaussianSplat', { state: LoadState.NotLoaded });
            return;
          case 'failed':
            return handleQueryError(
              'gaussianSplat',
              setDataState,
              splatsListQuery.error
            );
          case 'loading':
            setDataState('gaussianSplat', { state: LoadState.Loading });
            return;
          case 'ready':
            logger.debug(
              `Gaussian splats loaded, count: ${Object.keys(update.items).length}`,
              update.items
            );
            setDataState('gaussianSplat', {
              state: LoadState.LoadComplete,
              items: update.items,
            });
            return;
          default:
            throw new UnreachableCaseError(update);
        }
      }
    )
  );

  const loggedMissingReferences = new Set<string>();

  createEffect(() => {
    if (dataState.trajectory.state !== LoadState.LoadComplete) return;

    const missionState = dataState.mission.state;
    const pointCloudState = dataState.pointCloud.state;
    const missions =
      missionState === LoadState.LoadComplete
        ? dataState.mission.items
        : undefined;
    const pointClouds =
      pointCloudState === LoadState.LoadComplete
        ? dataState.pointCloud.items
        : undefined;

    const resolveSplittability = (
      trajectory: TrajectoryItem
    ): boolean | undefined => {
      if (
        missionState === LoadState.Error ||
        pointCloudState === LoadState.Error
      ) {
        return false;
      }
      if (!missions || !pointClouds) return undefined;

      const mission = Object.values(missions).find(
        (m) => m.pathId === trajectory.id
      );
      if (!mission) return false;
      if (mission.runs.length === 0) return false;

      let allRunsValid = true;
      for (const run of mission.runs) {
        const hasPlaceholderStations =
          run.beginStation === 0 &&
          (run.endStation === 0 || run.endStation === 1);
        if (hasPlaceholderStations) {
          return false;
        }
        if (pointClouds[run.id] === undefined) {
          allRunsValid = false;
        }
      }
      return allRunsValid;
    };

    const trajectoryItems = dataState.trajectory.items;
    batch(() => {
      for (const trajectory of Object.values(trajectoryItems)) {
        const resolved = resolveSplittability(trajectory);
        if (resolved === undefined || trajectory.isSplittable === resolved) {
          continue;
        }
        setDataState(
          'trajectory',
          produce((trajectories) => {
            if (trajectories.state !== LoadState.LoadComplete) return;
            const item = trajectories.items[trajectory.id];
            if (item) item.isSplittable = resolved;
          })
        );
        logger.debug('resolved trajectory splittability', {
          trajectoryName: trajectory.name,
          trajectoryId: trajectory.id,
          isSplittable: resolved,
        });
      }
    });

    if (missions && pointClouds) {
      const trajectories = dataState.trajectory.items;
      for (const mission of Object.values(missions)) {
        for (const run of mission.runs) {
          if (pointClouds[run.id] !== undefined) continue;
          const key = `pc:${mission.id}:${run.id}`;
          if (loggedMissingReferences.has(key)) continue;
          loggedMissingReferences.add(key);
          logger.debug('Mission point cloud not found in project', {
            missionName: mission.name,
            runName: run.name,
            pointCloudId: run.id,
          });
        }

        if (
          mission.pathId != null &&
          trajectories[mission.pathId] === undefined
        ) {
          const key = `path:${mission.id}:${mission.pathId}`;
          if (!loggedMissingReferences.has(key)) {
            loggedMissingReferences.add(key);
            logger.debug('Mission trajectory not found in project', {
              missionName: mission.name,
              trajectoryId: mission.pathId,
            });
          }
        }
      }
    }
  });

  // Keep the legacy load-state slice populated while consumers migrate to the
  // project-scoped repository exposed by this context. Key on location+id so a
  // new project-details object for the same project does not dispose it.
  const projectDataScope = createMemo(() => {
    const region = selectedRegion();
    const project = selectedProject();
    if (region === undefined || project === undefined) {
      return undefined;
    }
    return `${region.location}:${project.id}`;
  });

  createEffect(
    on(projectDataScope, (scope) => {
      const controller = new AbortController();
      let inFlightStore: ProjectDataRepository | undefined;
      let publishedStore: ProjectDataRepository | undefined;
      onCleanup(() => {
        controller.abort();
        inFlightStore?.dispose();
        publishedStore?.dispose();
      });

      setProjectDataRepository(undefined);
      if (scope === undefined) {
        setDataState('tcFileFolder', { state: LoadState.NotLoaded });
        return;
      }

      const region = untrack(selectedRegion);
      const project = untrack(selectedProject);
      if (region === undefined || project === undefined) {
        setDataState('tcFileFolder', { state: LoadState.NotLoaded });
        return;
      }

      setDataState('tcFileFolder', { state: LoadState.Loading });
      void loadProjectFileFolderSnapshot({
        region,
        projectId: project.id,
        signal: controller.signal,
        getTcpsRegionClient,
        getProjectDetails: (nextRegion, projectId) =>
          tcTcpsApi.getProjectDetails(nextRegion, projectId),
        attachStore: (store) => {
          inFlightStore = store;
        },
      })
        .then((result) => {
          if (controller.signal.aborted) {
            result.repository.dispose();
            return;
          }
          publishedStore = result.repository;
          inFlightStore = undefined;
          setProjectDataRepository(() => publishedStore);
          setDataState('tcFileFolder', {
            state: LoadState.LoadComplete,
            items: result.items,
          });
        })
        .catch((error: unknown) => {
          if (controller.signal.aborted || isAbortError(error)) return;
          inFlightStore?.dispose();
          inFlightStore = undefined;
          setProjectDataRepository(undefined);
          handleQueryError('tcFileFolder', setDataState, error);
        });
    })
  );

  // Add classification scheme data to state.
  const classificationSchemeQuery = createMemo(() => {
    const region = selectedRegion();
    const project = selectedProject();
    if (region === undefined || project === undefined || !canFetchScanData()) {
      return undefined;
    }
    return scanDataApi.listClassificationSchemeQuery(region, project.id);
  });

  createEffect(
    on(
      [() => classificationSchemeQuery()?.status, classificationSchemeQuery],
      ([_, query]) => {
        if (!query) return;

        if (query.isError) {
          NotificationEventBus.emit({
            type: 'error',
            message: t('general.unhandledError'),
          });
          setClassificationSchemeState({
            state: LoadState.Error,
          });
          return;
        }

        if (query.isLoading || query.isFetching) {
          setClassificationSchemeState({
            state: LoadState.Loading,
          });
          return;
        }

        if (query.data) {
          const schemeItems = query.data.reduce<
            Record<number, ClassificationSchemeItem>
          >((acc, classificationScheme) => {
            acc[classificationScheme.id!] = {
              id: classificationScheme.id!,
              name: classificationScheme.name!,
              rgba: classificationScheme.rgba!,
              visible: classificationScheme.visible,
            };
            return acc;
          }, {});

          setClassificationSchemeState({
            state: LoadState.LoadComplete,
            items: schemeItems,
          });
        }
      }
    )
  );

  const trajectoryFormatting = createMemo(
    on(formattingSettings, (settings, prev): FormattingSettings => {
      return prev?.lengthUnit === settings.lengthUnit &&
        prev?.lengthDecimals === settings.lengthDecimals
        ? prev
        : settings;
    })
  );

  // Track formatting changes that require trajectories to reload.
  createEffect(
    on(
      trajectoryFormatting,
      () => {
        if (dataState.trajectory.state !== LoadState.LoadComplete) return;

        const itemsToReload = Object.values(dataState.trajectory.items)
          .filter((item) => isItemLoadingOrLoaded(item.loadState))
          .map((item) => item.id);

        if (itemsToReload.length > 0) {
          dataLoadingService.reload('trajectory', itemsToReload);
        }
      },
      { defer: true }
    )
  );

  // Stable snapshot that only changes when a relevant alignment setting value changes.
  // coordinateOrder is excluded — it's for measurement display, not trajectory rendering.
  const alignmentSnapshot = createMemo<AlignmentSettings>(
    () => ({ ...alignmentSettings() }),
    { ...alignmentSettings() },
    {
      equals: (a, b) =>
        a === b ||
        (a.labelFormat === b.labelFormat &&
          a.labelSymbol === b.labelSymbol &&
          a.verticalOffset === b.verticalOffset &&
          a.horizontalOffset === b.horizontalOffset &&
          a.labelSize === b.labelSize &&
          a.labelInterval === b.labelInterval),
    }
  );

  const alignmentReloadScheduler = new AlignmentReloadScheduler((type, items) =>
    dataLoadingService.reload(type, items)
  );
  onCleanup(() => alignmentReloadScheduler.dispose());

  createEffect(
    on(
      alignmentSnapshot,
      (settings, prev) => {
        if (dataState.trajectory.state !== LoadState.LoadComplete) return;

        const loadedItems = Object.values(dataState.trajectory.items)
          .filter((item) => isItemLoadingOrLoaded(item.loadState))
          .map((item) => item.id);

        if (loadedItems.length === 0) return;

        alignmentReloadScheduler.schedule(settings, prev, loadedItems);
      },
      { defer: true }
    )
  );

  createEffect(
    on(alignmentRefreshVersion, (_version, prev) => {
      if (prev === undefined) return;

      untrack(() => {
        if (dataState.trajectory.state !== LoadState.LoadComplete) return;

        const loadedItems = Object.values(dataState.trajectory.items)
          .filter((item) => isItemLoadingOrLoaded(item.loadState))
          .map((item) => item.id);

        if (loadedItems.length === 0) return;

        dataLoadingService.reload('trajectory', loadedItems);
      });
    })
  );

  const classificationSchemes = createMemo(() => {
    if (classificationSchemeState.state !== LoadState.LoadComplete) {
      return [];
    }

    return Object.values(classificationSchemeState.items).map((item) => ({
      id: item.id,
      label: item.name,
      color: item.rgba,
      visible: item.visible,
      percentage: 0,
    }));
  });

  const classStatistics = createMemo(() => {
    const loadedPointclouds =
      dataState.pointCloud.state === LoadState.LoadComplete
        ? Object.values(dataState.pointCloud.items).filter(
            (item) => item.loadState === LoadState.LoadComplete
          )
        : [];

    // Aggregate class statistics from all loaded pointclouds
    const classStats: ClassStatistics = {};
    loadedPointclouds.forEach((item) => {
      if (!item.classStatistics) {
        return;
      }
      Object.entries(item.classStatistics).forEach(([key, value]) => {
        const id = Number(key);
        classStats[id] = (classStats[id] ?? 0) + value;
      });
    });

    return classStats;
  });

  return (
    <DataContext.Provider
      value={{
        dataState,
        setDataState,
        activeItems,
        setActiveItems,
        dataLoadingService,
        classificationSchemes,
        classStatistics,
        currentLoadedCsib,
        currentLoadedCenter,
        currentLoadedBounds,
        loadedCsibs,
        loadedCenters,
        loadedCrsIssues,
        activeCrs,
        pointCloudCrsById,
        projectDataRepository,
      }}
    >
      {props.children}
    </DataContext.Provider>
  );
}

export interface DataContextType {
  dataState: ProjectDataState;
  setDataState: SetStoreFunction<ProjectDataState>;
  activeItems: ActiveItems;
  setActiveItems: SetStoreFunction<ActiveItems>;
  dataLoadingService: DataLoadingService;
  classificationSchemes(): PointCloudClassification[];
  classStatistics(): ClassStatistics;
  projectDataRepository: Accessor<ProjectDataRepository | undefined>;
  /**
   * Effective CSIB for the 2D basemap: the oldest loaded visible point cloud
   * with a CSIB and a center, else the project CRS. Same cloud as `activeCrs`.
   */
  currentLoadedCsib: Accessor<string | undefined>;
  /**
   * Effective world-coordinate center for the 2D basemap. Uses the point-cloud
   * bounding-box center when a cloud is loaded.
   */
  currentLoadedCenter: Accessor<Point2D | undefined>;
  /**
   * Tile-load bounds for the empty project map. Undefined when a point cloud
   * is loaded — the tile map then uses center ± 100 km.
   */
  currentLoadedBounds: Accessor<AxisAlignedBounds | undefined>;
  /**
   * Per-point-cloud CSIB — no cross-cloud fallback (unlike currentLoadedCsib).
   */
  loadedCsibs: Accessor<ReadonlyMap<string, string>>;
  loadedCenters: Accessor<ReadonlyMap<string, Point2D>>;
  /**
   * Per-point-cloud CRS issue, shown as tree-node status. A cloud is listed
   * when it has no CSIB, or when its CSIB differs from the project CRS.
   */
  loadedCrsIssues: Accessor<ReadonlyMap<string, CrsIssueKind>>;
  activeCrs: Accessor<ActiveCrs | undefined>;
  /** Loaded clouds, including hidden ones. Cleared on unload, not on hide. */
  pointCloudCrsById: Accessor<ReadonlyMap<string, PointCloudCrs>>;
}

export const DataContext = createContext<DataContextType>();

export function useDataContext(): DataContextType {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useDataContext must be used within a DataContextProvider');
  }
  return context;
}
